import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { OrderItemIncomingFormProps } from "@/features/order/form/OrderItemsIncomingForm";
import customAlert from "@/features/ui/alert/Alert";
import {
  DefaultApiWmsBulkIncomingPostRequest,
  DefaultApiWmsIncomingPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { OrderIncomingFormProps } from "../form/OrderIncomingForm";

export interface OrderItemsIncomingProps {
  setLoading: Dispatch<SetStateAction<boolean>>;
}

interface OrderItemsSubmitProps {
  item: OrderItemIncomingFormProps;
  targetLocationCode?: string | undefined;
}

export const useOrderItemsIncoming = (params: OrderItemsIncomingProps) => {
  const queryClient = useQueryClient();
  const { setLoading } = params;
  // 미래테크를 제외한 다른 업체에서는 incoming
  // 미래테크에서는 purchaseOrderStockSerial
  const { mutate: incomingMutate, isLoading } = useMutation(
    (params: DefaultApiWmsIncomingPostRequest) =>
      mutateInventories.incoming(params).mutationFn(undefined),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        queryClient.invalidateQueries(["purchaseOrderItems"]);
        queryClient.invalidateQueries(["purchaseOrders"]);
        customAlert("입고에 성공하였습니다.", "입고 성공", "green");
      },
      onError: (error: AxiosError<Error>) => {
        customAlert(error.response?.data.message ?? "입고에 실패하였습니다.", "입고 실패", "red");
      },
      onSettled: () => {
        setLoading(false);
      },
    },
  );

  const { mutate: bulkIncomingMutate } = useMutation(
    (params: DefaultApiWmsBulkIncomingPostRequest) =>
      mutateInventories.purchaseBulkIncoming(params).mutationFn(undefined),
    {
      onMutate: () => {
        setLoading(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        queryClient.invalidateQueries(["purchaseOrderItems"]);
        queryClient.invalidateQueries(["purchaseOrders"]);
        customAlert("입고에 성공하였습니다.", "입고 성공", "green");
      },
      onError: (error: AxiosError<Error>) => {
        customAlert(error.response?.data.message ?? "입고에 실패하였습니다.", "입고 실패", "red");
      },
      onSettled: () => {
        setLoading(false);
      },
    },
  );

  const orderItemsSubmit = ({ item, targetLocationCode }: OrderItemsSubmitProps) => {
    incomingMutate({
      workLogsWorkLogIdCancelStartPut200ResponseCancelConsumeInner: {
        itemCode: item.itemCode ?? "",
        locationCode: targetLocationCode ?? item.targetLocationCode ?? "",
        quantity: item?.inputValue?.toString() ?? item.inputEligibleQuantity?.toString() ?? "",
        lotInfo: item.lotInfo as any,
        actionFrom: "WEB",
        purchaseOrderItemId: item.id,
      },
    });
  };

  const orderItemsBulkSubmit = (items: OrderIncomingFormProps[]) => {
    bulkIncomingMutate({
      wmsBulkIncomingPostRequest: {
        data: items.map((item) => ({
          itemCode: item.itemCode ?? "",
          locationCode: item.targetLocationCode ?? "",
          quantity: item.inputValue?.toString() ?? item.inputEligibleQuantity?.toString() ?? "",
          lotId: item.lotId ?? 0,
          lotInfo: item.lotInfo as any,
          actionFrom: "WEB",
          purchaseOrderItemId: item.id ?? null,
        })),
      },
    });
  };

  return {
    orderItemsSubmit,
    orderItemsBulkSubmit,
    setLoading,
    isLoading,
  };
};
